import React from 'react';
import { Helmet } from '../../components/Helmet';
import { JSONLD } from '../../components/JSONLD';
import { PreloadImage } from '../../components/PreloadImage';
import { TrainingGuidePageContainer } from '../../containers/TrainingGuidePageContainer';
import { AnimalTypeSlug, BASE_URL } from '../../constants/app';
import { DEFAULT_CACHE_POLICY } from '../../constants/cache';
import { HttpHeaderKey } from '../../constants/http-header-key';
import { Routes } from '../../constants/routes';
import { useCanonicalUrl } from '../../hooks/useCanonicalUrl';
import { getTrainingGuide } from '../../hooks/api/useGetTrainingGuide';
import { breadcrumbsBuilder } from '../../utils/breadcrumbsBuilder';
import { extractHref } from '../../utils/extractHref';
import { requestHandler } from '../../utils/requestHandler';
import { tryCatch } from '../../utils/tryCatch';
import { getPrevNextUrl } from '../../utils/getPrevNextUrl';
import { CamelizedTrainingGuideResponse } from '../../types/app/training-guide';
import { Layout } from '../../components/Layout';
type TrainingGuideProps = CamelizedTrainingGuideResponse & {
  animalTypeSlug: string;
  nextPageUrl?: string;
  prevPageUrl?: string;
};
const TrainingGuide = (props: TrainingGuideProps) => {
  const {
    canonicalUrl
  } = useCanonicalUrl();
  const {
    heroImageUrl,
    metaDesc,
    metaTitle,
    nextPageUrl,
    prevPageUrl,
    structuredData,
    title
  } = props;
  return <>
      <Helmet canonical={canonicalUrl} title={metaTitle} description={metaDesc} twitter={{
      title,
      description: metaDesc,
      image: heroImageUrl
    }} openGraph={{
      title,
      description: metaDesc,
      image: heroImageUrl,
      type: 'article'
    }} prevPageUrl={prevPageUrl} nextPageUrl={nextPageUrl} robots="index, follow" data-sentry-element="Helmet" data-sentry-source-file="[slug].tsx" />

      <PreloadImage src={heroImageUrl} data-sentry-element="PreloadImage" data-sentry-source-file="[slug].tsx" />

      <Layout data-sentry-element="Layout" data-sentry-source-file="[slug].tsx">
        <TrainingGuidePageContainer {...props} data-sentry-element="TrainingGuidePageContainer" data-sentry-source-file="[slug].tsx" />
      </Layout>

      {/* Structured Data for SEO */}
      <JSONLD data-sentry-element="JSONLD" data-sentry-source-file="[slug].tsx">
        {structuredData.article}
      </JSONLD>

      <JSONLD data-sentry-element="JSONLD" data-sentry-source-file="[slug].tsx">
        {structuredData.breadcrumbs}
      </JSONLD>

      <JSONLD data-sentry-element="JSONLD" data-sentry-source-file="[slug].tsx">
        {structuredData.howto}
      </JSONLD>
    </>;
};
const handleGetServerSideProps = requestHandler({
  headers: {
    [HttpHeaderKey.CacheControl]: DEFAULT_CACHE_POLICY
  }
});
export const getServerSideProps = handleGetServerSideProps(async ({
  req,
  query
}) => {
  const {
    slug,
    page = 1
  } = query;
  const [article, error] = await tryCatch(() => getTrainingGuide(slug as string, AnimalTypeSlug.Dog, Number(page)));
  if (error) {
    // Error handled by handleGetServerSideProps HOC
    throw error;
  }
  const breadcrumbs = breadcrumbsBuilder().add({
    title: 'Training',
    url: Routes.TrainingGuides
  }).add({
    title: article.title
  }).build();
  const {
    nextPage,
    prevPage
  } = article;
  const href = extractHref(req, BASE_URL, prevPage, page);
  const {
    prevPageUrl,
    nextPageUrl
  } = getPrevNextUrl(prevPage, nextPage, href);
  return {
    props: {
      ...article,
      animalTypeSlug: AnimalTypeSlug.Dog,
      breadcrumbs,
      nextPageUrl,
      prevPageUrl
    }
  };
});
export default TrainingGuide;